import React from 'react';
import { useIntl } from 'react-intl';
import {
  Card, CardBody, CardFooter,
} from 'reactstrap';
import styled from 'styled-components';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';

const Shimmer = styled.span.attrs({
  className: 'placeholder placeholder-glow bg-darken',
})`
  height: ${(props) => props.height || '1rem'};
  width: ${(props) => props.width || '100%'};
  display: inline-block;
  border-radius: 0.25rem;
`;

const PriorityRow = styled.div.attrs({
  className: 'card mb-4',
})`
  box-sizing: content-box;
  padding-right: 3rem;
`;

/**
 * Placeholder component that displays a loading skeleton for the
 * TradeRulePrioritizeNominated page. Mirrors the layout of `index.jsx`: breadcrumbs,
 * heading with property controls, and the ordered priority-list row skeletons.
 * @returns {React.ReactElement} TradeRulePrioritizeNominatedPlaceholder component.
 */
function TradeRulePrioritizeNominatedPlaceholder() {
  const intl = useIntl();

  const propertiesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_prioritize_nominated.placeholder.breadcrumbs.properties.label',
    defaultMessage: 'Properties',
  });
  const tradeRulesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_prioritize_nominated.placeholder.breadcrumbs.trade_rules.label',
    defaultMessage: 'Trade Rules',
  });
  const prioritizeTradesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_prioritize_nominated.placeholder.breadcrumbs.prioritize_trades.label',
    defaultMessage: 'Prioritise Trades',
  });
  const managePriorityContextLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_prioritize_nominated.placeholder.heading.context.label',
    defaultMessage: 'Trade Rules - Manage Priority',
  });
  const peerToPeerTradesHeading = intl.formatMessage({
    id: 'trade_rule.trade_rule_prioritize_nominated.placeholder.trade_rule_prioritize_nominated_list.header.text',
    defaultMessage: 'Peer-to-Peer Trades',
  });

  return (
    <div aria-hidden="true">
      <Breadcrumbs
        breadcrumbs={[
          { name: propertiesBreadcrumbLabel, path: '/properties' },
          { name: <Shimmer width="10rem" /> },
          { name: tradeRulesBreadcrumbLabel },
          { name: prioritizeTradesBreadcrumbLabel },
        ]}
      />
      <Heading
        title={<Shimmer width="12rem" />}
        subtitle={<Shimmer width="18rem" />}
        context={managePriorityContextLabel}
        controls={(
          <div className="d-flex">
            <Shimmer width="8rem" height="2.25rem" />
            <div className="ms-2">
              <Shimmer width="8rem" height="2.25rem" />
            </div>
          </div>
        )}
      />

      <Card className="mt-4 mb-4">
        <CardBody>
          <h2>{peerToPeerTradesHeading}</h2>
          <h3 className="mt-3"><Shimmer width="14rem" /></h3>
          <p><Shimmer width="20rem" /></p>

          {[1, 2, 3].map((row) => (
            <PriorityRow key={`priority-row-${row}`}>
              <CardBody>
                <Shimmer width="8rem" height="1.25rem" />
                <div className="mt-2"><Shimmer width="60%" /></div>
                <div className="mt-2"><Shimmer width="40%" /></div>
                <div className="mt-2"><Shimmer width="50%" /></div>
              </CardBody>
            </PriorityRow>
          ))}
        </CardBody>
        <CardFooter className="d-flex">
          <div className="me-2">
            <Shimmer width="8rem" height="2.25rem" />
          </div>
          <div>
            <Shimmer width="8rem" height="2.25rem" />
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

export default TradeRulePrioritizeNominatedPlaceholder;
